import React from "react";
import {
  Alert,
  AutoComplete,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
} from "antd";
import { getContactProjects, getContactFormValues } from "./directory-utils";
import styles from "./directory.module.css";

const required = (label) => ({
  required: true,
  whitespace: true,
  message: `${label} is required.`,
});
const templateOptions = (templates = [], current) =>
  [...new Set([...templates, ...(current ? [current] : [])])].map((value) => ({
    value,
    label: value === "Default" ? "Apply Permission Template Later" : value,
    disabled: !templates.includes(value),
  }));

const ContactForm = ({
  editingContact,
  saving,
  error,
  companies,
  projectOptions,
  permissions,
  permissionsLoading,
  permissionsError,
  onRetryPermissions,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const isEditing = Boolean(editingContact);
  const projectIds = Form.useWatch("projectIds", form) || [];
  const projectEditScope = Form.useWatch("projectEditScope", form) || "all";
  const canSubmit = !permissionsLoading && !permissionsError;
  const currentValues = editingContact
    ? getContactFormValues(editingContact)
    : null;
  const unavailableProjects = editingContact
    ? getContactProjects(editingContact).filter(
        ({ value }) => !projectOptions.some((option) => option.value === value),
      )
    : [];
  const availableProjects = [
    ...projectOptions,
    ...unavailableProjects.map((project) => ({ ...project, disabled: true })),
  ];
  const assignedProjectIds = new Set(projectIds.map(String));
  const projectsAvailableForEditing = availableProjects.filter(
    ({ value, disabled }) => assignedProjectIds.has(String(value)) && !disabled,
  );
  return (
    <Modal
      open
      title={isEditing ? "Edit Contact Info" : "Create Company Contact"}
      width={720}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okText={isEditing ? "Save Changes" : "Create Contact"}
      confirmLoading={saving}
      okButtonProps={{ disabled: !canSubmit }}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      maskClosable={!saving}
      keyboard={!saving}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={
          currentValues || {
            projectIds: [],
            projectEditScope: "all",
            inviteeProjectsToEdit: [],
          }
        }
        onFinish={onSubmit}
        disabled={saving}
        requiredMark="optional"
        scrollToFirstError
        style={{ marginTop: 24 }}
      >
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ marginBottom: 20 }}
          />
        )}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[required("First name")]}
            >
              <Input autoComplete="given-name" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[required("Last name")]}
            >
              <Input autoComplete="family-name" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="inviteeEmail"
              label="Email"
              rules={[
                required("Email"),
                { type: "email", message: "Enter a valid email address." },
              ]}
            >
              <Input type="email" disabled={isEditing} autoComplete="email" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="inviteePhoneNumber"
              label="Phone"
              rules={
                isEditing
                  ? []
                  : [
                      {
                        validator: (_, value) =>
                          !value?.trim() || /^\+?\d{10}$/.test(value.trim())
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error(
                                  "Phone number must be exactly 10 digits.",
                                ),
                              ),
                      },
                    ]
              }
            >
              <Input type="tel" autoComplete="tel" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="inviteeCompany"
              label="Company"
              rules={[required("Company")]}
            >
              <AutoComplete
                options={companies}
                filterOption={(input, option) =>
                  option.value.toLowerCase().includes(input.toLowerCase())
                }
                onSelect={(_, option) =>
                  form.setFieldValue(
                    "inviteeCompanyAddress",
                    option.address || "",
                  )
                }
                placeholder="Select or enter a company"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="inviteeCompanyAddress" label="Company Address">
              <Input autoComplete="street-address" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="projectIds"
          label="Projects"
          extra={
            unavailableProjects.length
              ? "Only projects you belong to can be changed. Existing assignments outside your projects are preserved."
              : "Only projects you belong to are available. Leave empty for company-level access only."
          }
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            options={availableProjects}
            placeholder="Select projects"
            notFoundContent="No projects available"
            maxTagCount="responsive"
            onChange={(values) => {
              const assignedIds = new Set(values.map(String));
              form.setFieldValue(
                "inviteeProjectsToEdit",
                (form.getFieldValue("inviteeProjectsToEdit") || []).filter(
                  (id) => assignedIds.has(String(id)),
                ),
              );
            }}
          />
        </Form.Item>
        {isEditing && (
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="projectEditScope" label="Apply changes to">
                <Select
                  options={[
                    { value: "all", label: "All projects" },
                    {
                      value: "selected",
                      label: "Selected projects only",
                      disabled: !projectsAvailableForEditing.length,
                    },
                  ]}
                  onChange={(value) => {
                    if (value === "all")
                      form.setFieldValue("inviteeProjectsToEdit", []);
                  }}
                />
              </Form.Item>
            </Col>
            {projectEditScope === "selected" && (
              <Col xs={24} sm={12}>
                <Form.Item
                  name="inviteeProjectsToEdit"
                  label="Projects to apply changes to"
                  rules={[
                    {
                      required: true,
                      message: "Select at least one project.",
                      type: "array",
                      min: 1,
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={projectsAvailableForEditing}
                    placeholder="Select projects"
                    notFoundContent="No assigned projects available"
                    maxTagCount="responsive"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        )}
        {permissionsError && (
          <Alert
            type="error"
            showIcon
            message={permissionsError}
            action={
              <button
                type="button"
                className={styles.retryLink}
                onClick={onRetryPermissions}
              >
                Retry
              </button>
            }
            style={{ marginBottom: 20 }}
          />
        )}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="inviteeCompanyPermission"
              label="Company Permissions Template"
              rules={[required("Company permissions template")]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select a template"
                loading={permissionsLoading}
                options={templateOptions(
                  permissions.companyAssignablePermissions,
                  currentValues?.inviteeCompanyPermission,
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="inviteeProjectPermission"
              label="Project Permissions Template"
              extra={
                isEditing && projectEditScope === "selected"
                  ? "Applies only to the projects selected above."
                  : "Applies to all assigned projects."
              }
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Select a template"
                loading={permissionsLoading}
                options={templateOptions(
                  permissions.projectAssignablePermissions,
                  currentValues?.inviteeProjectPermission,
                )}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ContactForm;
